import type { BodyId, BodyStatus, GraphicsProfile, RenderMode, ScaleMode } from '../core/types';
import { getBody, AU_IN_METRES, DEFAULT_CENTRAL_BODY } from '../core/reference-data';
import { MIN_SECTOR_COUNT, MAX_SECTOR_COUNT, DEFAULT_SECTOR_COUNT } from '../core/sectors';
import {
  ECCENTRICITY_MIN,
  ECCENTRICITY_MAX,
  SEMI_MAJOR_AXIS_MIN_AU,
  SEMI_MAJOR_AXIS_MAX_AU,
  SPEED_MIN_DAYS_PER_SECOND,
  SPEED_MAX_DAYS_PER_SECOND
} from '../core/ranges';
import { orbitalPeriod } from '../core/kepler';
import { orbitalSpeed, meanAnomalyAtWorldPoint, apoapsisRadius } from '../core/orbit-geometry';
import { effectiveMeanAnomalyAtTime, effectiveWorldPosition } from './phase';
import type { AppState } from './default-state';

/**
 * State-layer selectors (AD-2 layer table): the UI depends on `state` only. These
 * selectors read the fixed core reference data internally (`getBody`) and return
 * plain presentation data, so `core` access stays confined to the `state` layer
 * and no `ui` module reaches into `core`. They compute nothing new: colour and
 * name come straight from reference data, status from the store's runtime bodies.
 */

/**
 * Core unions re-exported through the `state` layer so `ui` can name them without
 * importing `core` directly (AD-2 layer table), mirroring the `AU_IN_METRES`
 * re-export below. `GraphicsProfile` / `RenderMode` are used by the settings and
 * canvas-host components (Story 1.6).
 */
export type { BodyId, BodyStatus, GraphicsProfile, RenderMode, ScaleMode };

/**
 * The single AU<->metres reference conversion factor, re-exported from `core`
 * through the `state` layer so `ui` can convert at the readout boundary (AD-5)
 * without importing `core` directly (AD-2 layer table), mirroring the `BodyStatus`
 * re-export above. `ui` must never re-declare this literal.
 */
export { AU_IN_METRES };

/**
 * Equal-time-sector count range (Story 2.1, FR-9), re-exported from `core` through
 * the `state` layer so the detail panel names the permitted range without importing
 * `core` directly (AD-2 layer table), mirroring the `AU_IN_METRES` re-export above.
 */
export { MIN_SECTOR_COUNT, MAX_SECTOR_COUNT, DEFAULT_SECTOR_COUNT };

/**
 * Editable semi-major-axis (AU) and playback-speed ranges, re-exported from the
 * canonical `core/ranges.ts` through the `state` layer so `ui` (format.ts,
 * PlaybackHud.svelte) names them WITHOUT importing `core` directly (AD-2 layer
 * table), mirroring the `AU_IN_METRES` / sector-count re-exports above. This keeps
 * one source (the codec's clamps provably equal the reducer's) while preserving the
 * ui->state-only rule. Eccentricity is re-exported alongside so the detail panel's
 * slider clamp names the canonical [0, 0.95] range instead of a local literal.
 */
export {
  ECCENTRICITY_MIN,
  ECCENTRICITY_MAX,
  SEMI_MAJOR_AXIS_MIN_AU,
  SEMI_MAJOR_AXIS_MAX_AU,
  SPEED_MIN_DAYS_PER_SECOND,
  SPEED_MAX_DAYS_PER_SECOND
};

/**
 * Playback-speed scale for the current central body. Orbital angular rate at a fixed
 * semi-major axis scales as sqrt(mu); when a low-mass planet is the centre its
 * bodies' periods balloon by sqrt(mu_sun / mu_central), so at a fixed days/sec the
 * orbits crawl. The stored `speedDaysPerSecond` is therefore a Sun-NORMALIZED base;
 * multiplying it by this scale gives the ACTUAL days/sec, so orbits run at the same
 * visual rate around ANY centre. The Sun returns exactly `1`, so the default view
 * (and every Sun-centre share/round-trip) is a byte-exact no-op. The factor cancels
 * the central mass in mean-anomaly space (`meanMotion(a, mu_central) * dt_scaled`
 * is independent of `mu_central`), so sub-step counts and clock precision are
 * unchanged from the Sun case.
 */
export function selectSpeedScale(state: AppState): number {
  const muCentral = getBody(state.centralBody).mu;
  const muSun = getBody('sun').mu;
  if (!(muCentral > 0) || !(muSun > 0)) return 1;
  return Math.sqrt(muSun / muCentral);
}

/** Actual playback speed in simulated days per real second (base * central scale). */
export function selectEffectiveSpeedDaysPerSecond(state: AppState): number {
  return state.speedDaysPerSecond * selectSpeedScale(state);
}

/** Plain presentation view of one body, ready for a legend row or a detail panel. */
export interface BodyView {
  readonly id: BodyId;
  readonly name: string;
  readonly colour: string;
  readonly status: BodyStatus;
  readonly isCentral: boolean;
  readonly isSelected: boolean;
}

function statusOf(state: AppState, id: BodyId): BodyStatus {
  return state.bodies.find((b) => b.id === id)?.status ?? 'active';
}

/** View data for a single body under the current state. */
export function selectBodyView(state: AppState, id: BodyId): BodyView {
  const body = getBody(id);
  return {
    id,
    name: body.name,
    colour: body.colour,
    status: statusOf(state, id),
    isCentral: state.centralBody === id,
    isSelected: state.selectedBody === id
  };
}

/** View data for every body, in the current legend display order. */
export function selectLegendRows(state: AppState): BodyView[] {
  return state.legendOrder.map((id) => selectBodyView(state, id));
}

/**
 * SI orbital readouts for one body under the current state (AD-2 layer table: the
 * UI reads these instead of touching `core`). All values are SI; AU / day / km-s
 * formatting happens only at the `ui` readout boundary (AD-5).
 *
 * The live element (`a`, `e`) is the store's single source of truth. Period and
 * speed use the CENTRAL body's mu (`getBody(state.centralBody).mu`), never the
 * orbiting body's (PRD 5.3). Distance and speed reflect the body's current phase
 * (`state.time`); at the paused default (time 0) the body sits at periapsis, so
 * r = a(1 - e). The central body does not orbit: its radial readouts are guarded
 * to finite zeros and `isCentral` is true, so the UI shows "not applicable".
 */
export interface OrbitalReadouts {
  readonly id: BodyId;
  /** Live eccentricity, dimensionless. */
  readonly e: number;
  /** Live semi-major axis, SI metres. */
  readonly a: number;
  /** Periapsis distance a(1 - e), SI metres. */
  readonly periapsis: number;
  /** Apoapsis distance a(1 + e), SI metres. */
  readonly apoapsis: number;
  /** Orbital period T = 2*pi*sqrt(a^3 / mu_central), SI seconds. */
  readonly period: number;
  /** Current focus distance r at `state.time`, SI metres. */
  readonly distance: number;
  /** Current orbital speed at `state.time`, SI metres per second. */
  readonly speed: number;
  /** True when this body is the current central body (does not orbit). */
  readonly isCentral: boolean;
}

function liveElements(state: AppState, id: BodyId): { a: number; e: number } {
  const body = state.bodies.find((b) => b.id === id);
  const seed = getBody(id);
  return { a: body?.a ?? seed.a, e: body?.e ?? seed.e };
}

/** SI orbital readouts for a single body under the current state. */
export function selectOrbitalReadouts(state: AppState, id: BodyId): OrbitalReadouts {
  const { a, e } = liveElements(state, id);
  const isCentral = state.centralBody === id;
  const muCentral = getBody(state.centralBody).mu;

  // The central body (a = 0) does not orbit; guard every radial readout to finite
  // zeros so no NaN/Infinity reaches the UI. The UI shows "not applicable".
  if (isCentral || a <= 0) {
    return { id, e, a, periapsis: 0, apoapsis: 0, period: 0, distance: 0, speed: 0, isCentral };
  }

  const periapsis = a * (1 - e);
  const apoapsis = a * (1 + e);
  const period = orbitalPeriod(a, muCentral);
  // Current position from the shared effective-phase path (Story 1.2 motion +
  // Story 1.7 dragged offset): distance/speed track the body's CURRENT phase,
  // including any dragged offset, so a drag that moves the body updates these
  // radial readouts. `a`, `e`, and period (the shareable params) are untouched by
  // phase (AC 1). At offset 0 this is byte-identical to the pre-1.7 path.
  const offset = state.phaseOffsets[id] ?? 0;
  const pos = effectiveWorldPosition({ a, e }, muCentral, state.time, offset);
  const distance = Math.hypot(pos.x, pos.y);
  const speed = orbitalSpeed(muCentral, distance, a);
  return { id, e, a, periapsis, apoapsis, period, distance, speed, isCentral };
}

/**
 * Focus-centred SI world position (metres) of a body under the current state,
 * including its dragged phase offset (Story 1.7). The ui reads this instead of
 * touching `core` (AD-2 layer table), then maps it to pixels via the render
 * projection (AD-10). The central / non-orbiting body sits at the focus origin.
 */
export function selectBodyWorldPosition(state: AppState, id: BodyId): { x: number; y: number } {
  if (state.centralBody === id) return { x: 0, y: 0 };
  const { a, e } = liveElements(state, id);
  const muCentral = getBody(state.centralBody).mu;
  const offset = state.phaseOffsets[id] ?? 0;
  return effectiveWorldPosition({ a, e }, muCentral, state.time, offset);
}

/**
 * The apoapsis world point (focus-centred SI metres) of the OUTERMOST active,
 * non-central orbiter — the body with the largest apoapsis, which "Fit all orbits"
 * must frame (Story 3.2, AC-3). Apoapsis lies on the -x axis (orbit-geometry
 * convention), so the point is `(-apoapsisRadius, 0)`. Returned through the state
 * layer so `ui` stays off `core` (AD-2). `null` when no orbiter qualifies.
 */
export function selectOutermostApoapsisWorldPoint(
  state: AppState
): { x: number; y: number } | null {
  let bestR = 0;
  for (const b of state.bodies) {
    if (b.id === state.centralBody || b.status !== 'active') continue;
    const r = apoapsisRadius({ a: b.a, e: b.e });
    if (r > bestR) bestR = r;
  }
  if (bestR <= 0) return null;
  return { x: -bestR, y: 0 };
}

/**
 * The vertical extremes (top and bottom world points, focus-centred SI metres) of
 * the OUTERMOST active orbit — the ellipse fit-all scales to. The framing must keep
 * these clear of the top bar and the Playback HUD (Story 3.2, AC-5). For the
 * body with the largest apoapsis, the ellipse centre sits at `(-a*e, 0)` and its
 * semi-minor is `b = a*sqrt(1 - e^2)`, so the extremes are `(-a*e, +/-b)`. This is
 * the point that actually tests the reserved inset (the apoapsis point lies on the
 * midline and never exercises it). `null` when no orbiter qualifies.
 */
export function selectOutermostOrbitVerticalExtremes(
  state: AppState
): { top: { x: number; y: number }; bottom: { x: number; y: number } } | null {
  let bestR = 0;
  let best: { a: number; e: number } | null = null;
  for (const b of state.bodies) {
    if (b.id === state.centralBody || b.status !== 'active') continue;
    const r = apoapsisRadius({ a: b.a, e: b.e });
    if (r > bestR) {
      bestR = r;
      best = { a: b.a, e: b.e };
    }
  }
  if (!best) return null;
  const centreX = -best.a * best.e;
  const semiMinor = best.a * Math.sqrt(1 - best.e * best.e);
  return {
    top: { x: centreX, y: semiMinor },
    bottom: { x: centreX, y: -semiMinor }
  };
}

/**
 * The body's current effective mean anomaly (radians) under the current state and
 * its dragged offset (Story 1.7). The drag inverse subtracts this from the target
 * mean anomaly to raise the short-arc `NudgePhase` delta.
 */
export function selectEffectiveMeanAnomaly(state: AppState, id: BodyId): number {
  const { a } = liveElements(state, id);
  const muCentral = getBody(state.centralBody).mu;
  const offset = state.phaseOffsets[id] ?? 0;
  return effectiveMeanAnomalyAtTime(a, muCentral, state.time, offset);
}

/**
 * Map a focus-centred SI world point to the mean anomaly of the corresponding
 * point on the body's ellipse (the pure `core` inverse, reached through the state
 * layer so `ui` stays off `core`, AD-2 layer table). Used by the pointer drag.
 */
export function selectMeanAnomalyAtWorldPoint(
  state: AppState,
  id: BodyId,
  wx: number,
  wy: number
): number {
  const { a, e } = liveElements(state, id);
  return meanAnomalyAtWorldPoint({ a, e }, wx, wy);
}

/**
 * The dragged phase offset (radians) for a body under the current state. Session-
 * only; never part of the shareable-config set (AD-4). Exposed for read-only ui
 * readouts and e2e evidence that a drag/step changed the phase.
 */
export function selectPhaseOffset(state: AppState, id: BodyId): number {
  return state.phaseOffsets[id] ?? 0;
}

/**
 * Equal-time-sector overlay settings for the detail panel and the accessible
 * summary (Story 2.1, AD-2 layer table: `ui` reads `state`, never `core`). The
 * overlay acts on the SELECTED body's orbit, so it is `applicable` only when the
 * selected body is a NON-central, ACTIVE orbiter: the central body has no orbit and
 * a removed (collided/absorbed) body has no live orbit. The built `Sector[]`
 * polygons are NOT exposed here: those are consumed by the render layer (which may
 * read `core` directly), never by `ui`.
 */
export interface SectorSettings {
  readonly visible: boolean;
  readonly count: number;
  readonly applicable: boolean;
}

/** Sector overlay settings under the current state. */
export function selectSectorSettings(state: AppState): SectorSettings {
  const selected = state.selectedBody;
  const isCentral = state.centralBody === selected;
  const status = statusOf(state, selected);
  return {
    visible: state.sectorOverlayVisible,
    count: state.sectorCount,
    applicable: !isCentral && status === 'active'
  };
}

/**
 * Plain diagnostics view data for the settings/diagnostics surface (Story 1.6,
 * AC 5, 7). The UI reads ONLY from `state` (AD-2 layer table), never from
 * `platform` or `render`. Frame rate is an ephemeral instrument reading joined in
 * the UI from the rAF sampler (never command-driven state), so it is not included
 * here. `requested !== active` tells the UI when Enhanced was requested but is not
 * live, so the interface can explain the active mode without ever claiming Enhanced.
 */
export interface Diagnostics {
  readonly activeRenderMode: RenderMode;
  readonly requestedProfile: GraphicsProfile;
  readonly reducedEffects: boolean;
  readonly unavailableFeatures: readonly string[];
}

/** Diagnostics view data under the current state. */
export function selectDiagnostics(state: AppState): Diagnostics {
  return {
    activeRenderMode: state.activeRenderMode,
    requestedProfile: state.requestedProfile,
    reducedEffects: state.reducedEffects,
    unavailableFeatures: state.unavailableFeatures
  };
}

/**
 * True when an alternate centre is active, i.e. the central body is not the
 * default Sun (Story 2.2, AC 8). The comparison uses the reference-data default
 * through the `state` layer so `ui` never re-declares the `'sun'` literal
 * (AD-2 layer table). Drives the experimental-mode messaging.
 */
export function selectIsAlternateCentre(state: AppState): boolean {
  return state.centralBody !== DEFAULT_CENTRAL_BODY;
}

/** Display name of the current central body (Story 2.2), read via reference data. */
export function selectCentreName(state: AppState): string {
  return getBody(state.centralBody).name;
}

/**
 * The active body-size scale mode (Story 3.3, FR-12), read via the `state` layer so
 * `ui` names it without importing `core` (AD-2 layer table). Shareable experiment
 * configuration; the settings surface reads this and dispatches `SetScaleMode`.
 */
export function selectScaleMode(state: AppState): ScaleMode {
  return state.scaleMode;
}
